import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, RefreshCw, Wrench } from 'lucide-react';
import { Layout } from '../components/Layout';
import { LoadingSpinner, PageLoader } from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { VISIBLE_AGENTIC_CODER_MODELS, type VisibleAgenticCoderModelId } from '../lib/agentModels';
import { dbInsert, dbUpdate, invokeFunction } from '../lib/api';

const ADMIN_EMAIL = 'a.burkov@gmail.com';

const CODER_MODELS = VISIBLE_AGENTIC_CODER_MODELS;

type CoderModelId = VisibleAgenticCoderModelId;

interface SubmissionListItem {
  id: string;
  base_tool_name: string;
  base_project_id: string | null;
  last_contract_refresh_at: string | null;
  created_at: string;
  updated_at: string;
}

interface SubmissionDetail {
  id: string;
  created_by: string;
  base_tool_name: string;
  base_tool_description: string;
  ideas_text: string;
  base_project_id: string | null;
  base_prompt: string | null;
  backend_contract: unknown;
  last_contract_refresh_at: string | null;
  created_at: string;
  updated_at: string;
}

interface CandidateDetail {
  id: string;
  submission_id: string;
  project_id: string | null;
  original_idea: string;
  title: string;
  description: string;
  slug: string;
  recipe_id: string | null;
  frontend_prompt_override: string | null;
  enabled: boolean;
  publish: boolean;
  logo_emoji: string | null;
  recipe_match_status: 'pending' | 'matched' | 'needs_backend_extension' | 'ambiguous';
  recipe_match_notes: string | null;
  backend_api_description: string | null;
  matched_recipe_contract: Record<string, unknown> | null;
  has_custom_code: boolean;
  code_updated_at: string | null;
  created_at: string;
  updated_at: string;
}

interface SubmissionPayload {
  submission: SubmissionDetail;
  candidates: CandidateDetail[];
  recipe_contracts: Array<Record<string, unknown>>;
}

interface PreparedFork {
  candidate_id: string;
  project_id: string;
  title: string;
  description: string;
  slug: string;
  publish: boolean;
  recipe_id: string;
  frontend_prompt_override: string | null;
  matched_recipe_contract: Record<string, unknown> | null;
}

interface AgenticAuthResponse {
  token: string;
  serviceUrl: string;
  projectId: string;
  conversationId: string;
  isFirstMessage: boolean;
  isForkedProject: boolean;
  isBaseToolFork: boolean;
  baseToolForkMap: Record<string, unknown> | null;
  codeZipBase64: string | null;
  codeZipGcsPath: string | null;
}

function formatDateTime(value: string | null): string {
  if (!value) return 'n/a';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'n/a';
  return parsed.toLocaleString();
}

function slugify(value: string): string {
  return value
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\p{M}\s-]+/gu, ' ')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

async function streamAgenticRun(
  targetServiceUrl: string,
  authToken: string,
  payload: Record<string, unknown>,
  onStatus: (message: string) => void,
): Promise<void> {
  const response = await fetch(`${targetServiceUrl}/run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to start coder agent run');
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Coder service returned no stream');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = '';
  let receivedTerminalEvent = false;

  const processLine = (line: string) => {
    if (line.startsWith('event: ')) {
      currentEvent = line.slice(7).trim();
      return;
    }

    if (!line.startsWith('data: ') || !currentEvent) return;

    let data: Record<string, unknown>;
    try {
      data = JSON.parse(line.slice(6));
    } catch {
      currentEvent = '';
      return;
    }

    if (currentEvent === 'status' && typeof data.message === 'string') {
      onStatus(data.message);
    }

    if (currentEvent === 'tool' && typeof data.name === 'string') {
      onStatus(`🔧 ${data.name}`);
    }

    if (currentEvent === 'text' && typeof data.content === 'string') {
      const trimmed = data.content.trim();
      if (trimmed) {
        onStatus(trimmed.length > 200 ? `${trimmed.slice(0, 200)}…` : trimmed);
      }
    }

    if (currentEvent === 'sibling_forks_modified') {
      const projectIds = Array.isArray(data.projectIds) ? data.projectIds as string[] : [];
      if (projectIds.length > 0) {
        onStatus(`⚠️ ${projectIds.length} sibling fork(s) were also modified. Check them:`);
        for (const pid of projectIds) {
          onStatus(`  → /project/${pid}`);
        }
      }
    }

    if (currentEvent === 'error') {
      receivedTerminalEvent = true;
      throw new Error(typeof data.message === 'string' ? data.message : 'Coder agent failed');
    }

    if (currentEvent === 'complete') {
      receivedTerminalEvent = true;
      onStatus('Coder agent completed.');
      return;
    }

    currentEvent = '';
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      const remainingLines = buffer ? buffer.split('\n') : [];
      buffer = '';
      for (const line of remainingLines) {
        processLine(line);
      }
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      processLine(line);
      if (receivedTerminalEvent) {
        return;
      }
    }
  }

  if (receivedTerminalEvent) {
    return;
  }

  throw new Error('Coder agent stream ended before reporting completion.');
}

function buildBasePrompt(submission: SubmissionDetail, candidates: CandidateDetail[]): string {
  const forkIdeas = candidates
    .filter((c) => c.enabled)
    .map((c) => ({
      name: c.title,
      description: c.description,
    }));

  return `Build or update the hidden base mini-app for the tool "${submission.base_tool_name}".

Base tool description:
${submission.base_tool_description}

The admin is only identifying which tool this is. You already know the Llambada platform architecture and should infer the best recipe design yourself.

Before defining recipes or command templates:
- You MUST use web search to find and read the current official documentation for ${submission.base_tool_name}.
- Search for: installation instructions, CLI command names, flags, parameter formats, input/output behavior, and Python API usage.
- Cross-check every command invocation, flag, and parameter against the official docs — do NOT guess or rely on memory.
- If the tool has a Python library, search for its API reference and verify function signatures, arguments, and return types.

This base mini-app must provide one shared backend that powers many fork mini-apps with different frontends.

The following fork mini-apps are planned. Design recipes to support them. You decide the recipe_ids, structure, and count. Multiple forks may share a single recipe if appropriate.
${JSON.stringify(forkIdeas, null, 2)}

Required files:
- backend/main.py
- backend/requirements.txt
- backend/Dockerfile
- backend/recipes.json
- frontend/index.html

Backend requirements:
1. Install and use ${submission.base_tool_name} on the backend.
2. Use web search to read the current official documentation for ${submission.base_tool_name} before writing any backend code. Verify every CLI flag, parameter, and Python API call against the docs.
3. Do NOT expose raw command-line execution to the frontend.
4. The frontend must submit structured JSON or form fields; the backend converts them into safe command invocations.
5. Design recipes to cover all the planned fork mini-apps listed above.
6. Keep the backend generic so many fork frontends can use it.

backend/recipes.json requirements:
- Must be valid JSON.
- Must include a top-level "recipes" array.
- Each recipe object must include:
  - recipe_id (snake_case identifier)
  - title
  - description (what this recipe does)
  - input_contract (short label like "single image", "animated gif", "multiple images")
  - output_contract (short label for what the backend returns)
  - params (object describing accepted parameters with types, defaults, and constraints)
  - command (the CLI command or Python function name used)
  - args_template (how params map to command arguments)

Frontend requirements for the hidden base mini-app:
- Provide a simple internal tester UI that helps the admin exercise the shared backend.
- It does not need to look like the public forks.

Important:
- Keep backward compatibility with any existing recipe_id that already exists.
- Return a complete working mini-app ready for deployment.`;
}

function buildForkFrontendPrompt(candidate: CandidateDetail): string {
  const backendDescription = candidate.backend_api_description || JSON.stringify(candidate.matched_recipe_contract || {}, null, 2);

  if (candidate.frontend_prompt_override?.trim()) {
    return candidate.frontend_prompt_override.trim();
  }

  return `Modify only frontend/index.html for this fork mini-app.

Mini-app title:
${candidate.title}

Mini-app description:
${candidate.description}

This fork uses a shared Cloud Run service (Dockerfile + requirements.txt from parent) but has its own backend/main.py and frontend/index.html.

Fixed recipe_id for this fork:
${candidate.recipe_id}

Backend API description for this recipe:
${backendDescription}

Requirements:
- Build a polished, public-facing frontend specific to this fork idea.
- The UI must reflect the title and description above.
- Always send the fixed recipe_id "${candidate.recipe_id}" when submitting.
- Implement the backend logic for this recipe in backend/main.py.
- Show clear validation, progress, result preview, and download actions when appropriate.
- Keep the frontend focused on this single mini-app, not a generic multi-tool UI.
- Do not modify backend/Dockerfile, backend/requirements.txt, or backend/recipes.json — those belong to the parent project.`;
}

function buildExtendRecipesPrompt(submission: SubmissionDetail, unmatchedCandidates: CandidateDetail[]): string {
  const unmatchedList = unmatchedCandidates.map((c) => ({
    name: c.title,
    description: c.description,
  }));

  return `Extend the existing backend for the tool "${submission.base_tool_name}" with additional recipes.

Base tool description:
${submission.base_tool_description}

Before adding recipes:
- You MUST use web search to find and read the current official documentation for ${submission.base_tool_name}.
- Cross-check every command invocation, flag, and parameter against the official docs — do NOT guess or rely on memory.

The following fork mini-apps do NOT yet have matching recipes in the backend. Add new recipes to backend/recipes.json and implement them in backend/main.py to support these:
${JSON.stringify(unmatchedList, null, 2)}

Requirements:
- Do NOT remove or modify existing recipes — only ADD new ones.
- Each new recipe must follow the same structure as existing recipes in recipes.json.
- Update backend/main.py to implement the new recipes.
- Update backend/requirements.txt and backend/Dockerfile if new dependencies are needed.
- Keep backward compatibility with all existing recipes.`;
}

export function AdminBaseToolForkGeneratorPage() {
  const { user, session, loading: authLoading, ensureFreshToken } = useAuth();

  const [submissions, setSubmissions] = useState<SubmissionListItem[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string>('');
  const [submissionDetail, setSubmissionDetail] = useState<SubmissionDetail | null>(null);
  const [candidates, setCandidates] = useState<CandidateDetail[]>([]);
  const [recipeContracts, setRecipeContracts] = useState<Array<Record<string, unknown>>>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  const [baseToolName, setBaseToolName] = useState('');
  const [baseToolDescription, setBaseToolDescription] = useState('');
  const [ideasText, setIdeasText] = useState('');

  const [coderModel, setCoderModel] = useState<CoderModelId>('claude-opus-4-6');
  const [drafting, setDrafting] = useState(false);
  const [savingSubmission, setSavingSubmission] = useState(false);
  const [savingCandidateId, setSavingCandidateId] = useState<string | null>(null);
  const [creatingBase, setCreatingBase] = useState(false);
  const [refreshingContract, setRefreshingContract] = useState(false);
  const [recreatingForks, setRecreatingForks] = useState(false);
  const [baseStatus, setBaseStatus] = useState<string[]>([]);
  const [forkStatus, setForkStatus] = useState<string[]>([]);
  const [forkProgress, setForkProgress] = useState<{ current: number; total: number; title: string } | null>(null);
  const [forkCoderLines, setForkCoderLines] = useState<string[]>([]);

  const appendBaseStatus = useCallback((line: string) => {
    setBaseStatus((prev) => [...prev, line]);
  }, []);

  const appendForkStatus = useCallback((line: string) => {
    setForkStatus((prev) => [...prev, line]);
  }, []);

  const loadSubmissions = useCallback(async () => {
    if (!session) return;
    setLoadingSubmissions(true);
    const { data, error } = await invokeFunction<{ submissions: SubmissionListItem[] }>(
      'manage-base-tool-forks',
      { action: 'list_submissions' },
      { token: session.access_token },
    );

    if (error || !data) {
      setPageError(error?.message || 'Failed to load submissions');
      setSubmissions([]);
    } else {
      setSubmissions(data.submissions || []);
      if (!selectedSubmissionId && data.submissions?.length) {
        setSelectedSubmissionId(data.submissions[0].id);
      }
    }
    setLoadingSubmissions(false);
  }, [session, selectedSubmissionId]);

  const loadSubmission = useCallback(async (submissionId: string) => {
    if (!session || !submissionId) return;
    setLoadingDetail(true);
    setPageError(null);

    const { data, error } = await invokeFunction<SubmissionPayload>(
      'manage-base-tool-forks',
      { action: 'get_submission', submissionId },
      { token: session.access_token },
    );

    if (error || !data) {
      setPageError(error?.message || 'Failed to load submission');
      setSubmissionDetail(null);
      setCandidates([]);
      setRecipeContracts([]);
    } else {
      setSubmissionDetail(data.submission);
      setCandidates(data.candidates || []);
      setRecipeContracts(data.recipe_contracts || []);
      setBaseToolName(data.submission.base_tool_name || '');
      setBaseToolDescription(data.submission.base_tool_description || '');
      setIdeasText(data.submission.ideas_text || '');
    }
    setLoadingDetail(false);
  }, [session]);

  useEffect(() => {
    document.title = 'Base Tool Fork Generator - Llambada';
    return () => {
      document.title = 'Llambada';
    };
  }, []);

  useEffect(() => {
    if (user?.email === ADMIN_EMAIL) {
      loadSubmissions();
    }
  }, [user, loadSubmissions]);

  useEffect(() => {
    if (selectedSubmissionId) {
      loadSubmission(selectedSubmissionId);
    }
  }, [selectedSubmissionId, loadSubmission]);

  const enabledCandidates = useMemo(
    () => candidates.filter((c) => c.enabled),
    [candidates],
  );

  const matchedCandidates = useMemo(
    () => enabledCandidates.filter((c) => c.recipe_match_status === 'matched'),
    [enabledCandidates],
  );

  const unmatchedCandidates = useMemo(
    () => enabledCandidates.filter((c) => c.recipe_match_status !== 'matched'),
    [enabledCandidates],
  );

  const recreatableCandidates = useMemo(
    () => matchedCandidates.filter((c) => !c.has_custom_code),
    [matchedCandidates],
  );

  const resetForNewSubmission = () => {
    setSelectedSubmissionId('');
    setSubmissionDetail(null);
    setCandidates([]);
    setRecipeContracts([]);
    setBaseToolName('');
    setBaseToolDescription('');
    setIdeasText('');
    setBaseStatus([]);
    setForkStatus([]);
    setPageError(null);
  };

  const applyPayload = (payload: SubmissionPayload) => {
    setSubmissionDetail(payload.submission);
    setCandidates(payload.candidates || []);
    setRecipeContracts(payload.recipe_contracts || []);
    setBaseToolName(payload.submission.base_tool_name || '');
    setBaseToolDescription(payload.submission.base_tool_description || '');
    setIdeasText(payload.submission.ideas_text || '');
    setSelectedSubmissionId(payload.submission.id);
  };

  const handleDraft = async (e: FormEvent) => {
    e.preventDefault();
    if (!session) return;

    setDrafting(true);
    setPageError(null);

    const { data, error } = await invokeFunction<SubmissionPayload>(
      'manage-base-tool-forks',
      {
        action: 'draft_submission',
        submissionId: submissionDetail?.id || null,
        baseToolName,
        baseToolDescription,
        ideasText,
      },
      { token: session.access_token },
    );

    if (error || !data) {
      setPageError(error?.message || 'Failed to save ideas');
    } else {
      applyPayload(data);
      await loadSubmissions();
    }

    setDrafting(false);
  };

  const handleSaveSubmission = async () => {
    if (!session || !submissionDetail) return;
    setSavingSubmission(true);
    setPageError(null);

    const { data, error } = await invokeFunction<SubmissionPayload>(
      'manage-base-tool-forks',
      {
        action: 'update_submission',
        submissionId: submissionDetail.id,
        baseToolName,
        baseToolDescription,
        ideasText,
      },
      { token: session.access_token },
    );

    if (error || !data) {
      setPageError(error?.message || 'Failed to save submission');
    } else {
      applyPayload(data);
      await loadSubmissions();
    }

    setSavingSubmission(false);
  };

  const handleSaveCandidate = async (candidate: CandidateDetail) => {
    if (!session) return;
    setSavingCandidateId(candidate.id);
    setPageError(null);

    const { data, error } = await invokeFunction<SubmissionPayload>(
      'manage-base-tool-forks',
      {
        action: 'update_candidate',
        candidateId: candidate.id,
        title: candidate.title,
        description: candidate.description,
        slug: candidate.slug,
        recipeId: candidate.recipe_id,
        frontendPromptOverride: candidate.frontend_prompt_override,
        logoEmoji: candidate.logo_emoji,
        enabled: candidate.enabled,
        publish: candidate.publish,
      },
      { token: session.access_token },
    );

    if (error || !data) {
      setPageError(error?.message || `Failed to save ${candidate.title}`);
    } else {
      applyPayload(data);
      await loadSubmissions();
    }

    setSavingCandidateId(null);
  };

  const runCoderForProject = async (projectId: string, prompt: string, onStatus: (message: string) => void, accessToken: string) => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

    const { data: conversation, error: conversationError } = await dbInsert<{ id: string }>(
      'conversations',
      { project_id: projectId, title: 'Base tool automation' },
      { token: accessToken },
    );
    if (conversationError || !conversation) {
      throw new Error(conversationError?.message || 'Failed to create conversation');
    }

    const { data: userMessage, error: userMessageError } = await dbInsert<{ id: string }>(
      'messages',
      {
        conversation_id: conversation.id,
        role: 'user',
        content: prompt,
      },
      { token: accessToken },
    );
    if (userMessageError || !userMessage) {
      throw new Error(userMessageError?.message || 'Failed to create user message');
    }

    const authResponse = await fetch(`${supabaseUrl}/functions/v1/agentic-coder-auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        conversationId: conversation.id,
        message: prompt,
        currentUserMessageId: userMessage.id,
      }),
    });

    if (!authResponse.ok) {
      const text = await authResponse.text();
      throw new Error(text || 'Failed to authenticate coder agent');
    }

    const authData = await authResponse.json() as AgenticAuthResponse;

    await streamAgenticRun(
      authData.serviceUrl,
      authData.token,
      {
        message: prompt,
        conversationId: authData.conversationId,
        currentUserMessageId: userMessage.id,
        projectId: authData.projectId,
        isFirstMessage: authData.isFirstMessage,
        isForkedProject: authData.isForkedProject,
        isBaseToolFork: authData.isBaseToolFork || false,
        baseToolForkMap: authData.baseToolForkMap || null,
        codeZipBase64: authData.codeZipBase64,
        codeZipGcsPath: authData.codeZipGcsPath,
        model: coderModel,
      },
      onStatus,
    );
  };

  const handleBuildAndMatch = async () => {
    if (!session || !user) return;
    if (!baseToolName.trim() || !baseToolDescription.trim() || !ideasText.trim()) {
      setPageError('Fill in the tool name, description, and at least one idea.');
      return;
    }

    setCreatingBase(true);
    setPageError(null);
    setBaseStatus([]);

    const getFreshToken = async (): Promise<string> => {
      const token = await ensureFreshToken();
      if (!token) throw new Error('Session expired. Please sign in again and retry.');
      return token;
    };

    try {
      // Step 1: Save ideas (create or update submission + candidates)
      appendBaseStatus('Saving ideas...');
      let token = await getFreshToken();
      const { data: draftData, error: draftError } = await invokeFunction<SubmissionPayload>(
        'manage-base-tool-forks',
        {
          action: 'draft_submission',
          submissionId: submissionDetail?.id || null,
          baseToolName,
          baseToolDescription,
          ideasText,
        },
        { token },
      );
      if (draftError || !draftData) {
        throw new Error(draftError?.message || 'Failed to save ideas');
      }
      applyPayload(draftData);
      await loadSubmissions();

      const currentSubmission = draftData.submission;
      const currentCandidates = draftData.candidates;

      // Step 2: Create base project if needed
      let baseProjectId = currentSubmission.base_project_id;
      if (!baseProjectId) {
        appendBaseStatus('Creating hidden base project...');
        token = await getFreshToken();
        const { data: project, error: projectError } = await dbInsert<{ id: string }>(
          'projects',
          {
            user_id: user.id,
            name: `${currentSubmission.base_tool_name} Base`,
            description: `Hidden shared backend for ${currentSubmission.base_tool_name}`,
            is_published: false,
            type: 'utility',
          },
          { token },
        );
        if (projectError || !project) {
          throw new Error(projectError?.message || 'Failed to create base project');
        }
        baseProjectId = project.id;

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        appendBaseStatus('Initializing project files...');
        const initResponse = await fetch(`${supabaseUrl}/functions/v1/project-code?projectId=${baseProjectId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'init' }),
        });
        if (!initResponse.ok) {
          throw new Error(await initResponse.text() || 'Failed to initialize project code');
        }

        appendBaseStatus('Provisioning shared backend infrastructure...');
        const { error: infraError } = await invokeFunction(
          'create-project-infrastructure',
          { projectId: baseProjectId, projectName: `${currentSubmission.base_tool_name} Base` },
          { token },
        );
        if (infraError) {
          appendBaseStatus(`Infrastructure warning: ${infraError.message}`);
        }

        const { data: attachData, error: attachError } = await invokeFunction<SubmissionPayload>(
          'manage-base-tool-forks',
          {
            action: 'attach_base_project',
            submissionId: currentSubmission.id,
            baseProjectId,
          },
          { token },
        );
        if (attachError || !attachData) {
          throw new Error(attachError?.message || 'Failed to attach base project');
        }
        applyPayload(attachData);
        await loadSubmissions();
      }

      // Step 3: Run coder to build the base backend
      appendBaseStatus('Running coder agent on the hidden base project...');
      token = await getFreshToken();
      const basePrompt = buildBasePrompt(currentSubmission, currentCandidates);
      await runCoderForProject(baseProjectId, basePrompt, appendBaseStatus, token);

      // Step 4: Refresh recipes from GCS + AI matching
      appendBaseStatus('Refreshing recipes and matching candidates...');
      token = await getFreshToken();
      const { data: refreshed, error: refreshError } = await invokeFunction<SubmissionPayload>(
        'manage-base-tool-forks',
        {
          action: 'refresh_contract',
          submissionId: currentSubmission.id,
        },
        { token },
      );
      if (refreshError || !refreshed) {
        throw new Error(refreshError?.message || 'Failed to refresh and match recipes');
      }
      applyPayload(refreshed);
      await loadSubmissions();

      // Step 5: If unmatched candidates remain, extend recipes and re-match
      const stillUnmatched = (refreshed.candidates || []).filter(
        (c: CandidateDetail) => c.enabled && c.recipe_match_status !== 'matched'
      );
      if (stillUnmatched.length > 0) {
        appendBaseStatus(`${stillUnmatched.length} candidates unmatched. Extending recipes...`);
        token = await getFreshToken();
        const extendPrompt = buildExtendRecipesPrompt(refreshed.submission, stillUnmatched);
        await runCoderForProject(baseProjectId, extendPrompt, appendBaseStatus, token);

        appendBaseStatus('Re-matching candidates after recipe extension...');
        token = await getFreshToken();
        const { data: reMatched, error: reMatchError } = await invokeFunction<SubmissionPayload>(
          'manage-base-tool-forks',
          {
            action: 'refresh_contract',
            submissionId: currentSubmission.id,
          },
          { token },
        );
        if (reMatchError || !reMatched) {
          throw new Error(reMatchError?.message || 'Failed to re-match after extension');
        }
        applyPayload(reMatched);
        await loadSubmissions();

        const finalUnmatched = (reMatched.candidates || []).filter(
          (c: CandidateDetail) => c.enabled && c.recipe_match_status !== 'matched'
        );
        if (finalUnmatched.length > 0) {
          appendBaseStatus(`Warning: ${finalUnmatched.length} candidates still unmatched after extension.`);
        }
      }

      appendBaseStatus('Done. All steps completed.');
    } catch (error) {
      setPageError(error instanceof Error ? error.message : 'Failed to build and match');
    } finally {
      setCreatingBase(false);
    }
  };

  const handleRefreshAndMatch = async () => {
    if (!session || !submissionDetail) return;
    setRefreshingContract(true);
    setPageError(null);

    const { data, error } = await invokeFunction<SubmissionPayload>(
      'manage-base-tool-forks',
      {
        action: 'refresh_contract',
        submissionId: submissionDetail.id,
      },
      { token: session.access_token },
    );

    if (error || !data) {
      setPageError(error?.message || 'Failed to refresh and match recipes');
    } else {
      applyPayload(data);
      await loadSubmissions();
    }

    setRefreshingContract(false);
  };

  const handleRecreateForks = async () => {
    if (!session || !submissionDetail) return;
    if (recreatableCandidates.length === 0) {
      setPageError('No eligible fork candidates. All matched forks already have custom code.');
      return;
    }

    setRecreatingForks(true);
    setPageError(null);
    setForkStatus([]);
    setForkProgress(null);
    setForkCoderLines([]);

    const total = recreatableCandidates.length;

    try {
      for (let i = 0; i < total; i++) {
        const candidate = recreatableCandidates[i];

        // Refresh token before each fork to prevent expiry during long runs
        const freshToken = await ensureFreshToken();
        if (!freshToken) {
          throw new Error('Session expired. Please sign in again and retry.');
        }

        setForkProgress({ current: i + 1, total, title: candidate.title });
        setForkCoderLines([`Preparing fork project for "${candidate.title}"...`]);

        // Prepare this single fork (create project + sync code)
        const { data: preparedData, error: prepareError } = await invokeFunction<{ prepared_forks: PreparedFork[] }>(
          'manage-base-tool-forks',
          {
            action: 'prepare_forks',
            submissionId: submissionDetail.id,
            candidateIds: [candidate.id],
          },
          { token: freshToken },
        );

        if (prepareError || !preparedData) {
          throw new Error(prepareError?.message || `Failed to prepare fork for "${candidate.title}"`);
        }

        const preparedFork = preparedData.prepared_forks?.[0];
        if (!preparedFork) {
          appendForkStatus(`[${i + 1}/${total}] Skipped "${candidate.title}" (not matched)`);
          continue;
        }

        // Run coder to generate frontend
        const onCoderOutput = (line: string) => {
          setForkCoderLines((prev) => {
            const next = [...prev, line];
            return next.length > 100 ? next.slice(-100) : next;
          });
        };

        await runCoderForProject(preparedFork.project_id, buildForkFrontendPrompt(candidate), onCoderOutput, freshToken);

        // Publish or keep private
        await dbUpdate(
          'projects',
          { is_published: preparedFork.publish },
          { id: `eq.${preparedFork.project_id}` },
          { token: freshToken, returning: false },
        );

        appendForkStatus(
          `[${i + 1}/${total}] ${preparedFork.publish ? 'Published' : 'Updated'} "${preparedFork.title}"`,
        );
      }

      setForkProgress(null);
      setForkCoderLines([]);
      appendForkStatus(`Done. ${total} fork mini-apps processed.`);
      await loadSubmission(submissionDetail.id);
      await loadSubmissions();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to recreate fork mini-apps';
      setPageError(message);
      setForkCoderLines((prev) => [...prev, `ERROR: ${message}`]);
    } finally {
      setRecreatingForks(false);
      setForkProgress(null);
    }
  };

  if (authLoading) return <PageLoader />;
  if (!user || user.email !== ADMIN_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-theme-surface hover:bg-theme-bg-muted transition-colors"
            title="Back to Admin"
          >
            <ArrowLeft className="h-5 w-5 text-theme-text-muted" />
          </Link>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500">
            <Wrench className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-theme-text">Base Tool Fork Generator</h1>
            <p className="text-theme-text-secondary text-sm">
              Save fork ideas, build one hidden base backend, then match recipes and recreate fork frontends.
            </p>
          </div>
        </div>

        {pageError && (
          <div className="rounded-lg border border-theme-error-border bg-theme-error-bg px-4 py-3 text-sm text-theme-error">
            {pageError}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-6">
          <form onSubmit={handleDraft} className="card p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-theme-text">Submission</h2>
              <div className="flex gap-2">
                {submissionDetail && (
                  <button
                    type="button"
                    onClick={handleSaveSubmission}
                    className="btn btn-secondary"
                    disabled={savingSubmission}
                  >
                    {savingSubmission ? <LoadingSpinner size="sm" /> : 'Save Metadata'}
                  </button>
                )}
                <button type="button" onClick={resetForNewSubmission} className="btn btn-secondary">
                  New
                </button>
              </div>
            </div>

            <label className="block">
              <span className="text-sm font-medium text-theme-text-secondary">Base tool name</span>
              <input
                className="input mt-2"
                value={baseToolName}
                onChange={(e) => setBaseToolName(e.target.value)}
                placeholder="ImageMagick"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-theme-text-secondary">Base tool description</span>
              <textarea
                className="input mt-2 min-h-[140px]"
                value={baseToolDescription}
                onChange={(e) => setBaseToolDescription(e.target.value)}
                placeholder="Describe the base tool so the coder can identify it and research its documentation. Example: ImageMagick, the command-line suite for image conversion, GIF animation, filters, compositing, and metadata operations."
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-theme-text-secondary">Mini-app ideas (name + optional description, separated by empty lines)</span>
              <textarea
                className="input mt-2 min-h-[220px]"
                value={ideasText}
                onChange={(e) => setIdeasText(e.target.value)}
                placeholder={'Blur an Image\nApply gaussian or box blur to photos\n\nAdd Border\n\nGIF Reverser\nReverse the frame order of an animated GIF'}
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-theme-text-secondary">Coder model</span>
              <select
                className="input mt-2"
                value={coderModel}
                onChange={(e) => setCoderModel(e.target.value as CoderModelId)}
              >
                {CODER_MODELS.map((model) => (
                  <option key={model.id} value={model.id}>{model.name}</option>
                ))}
              </select>
            </label>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleBuildAndMatch}
                disabled={creatingBase}
              >
                {creatingBase ? <LoadingSpinner size="sm" /> : submissionDetail?.base_project_id ? 'Rebuild & Match' : 'Build & Match'}
              </button>
              <button type="submit" className="btn btn-secondary" disabled={drafting}>
                {drafting ? <LoadingSpinner size="sm" /> : 'Save Ideas Only'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleRefreshAndMatch}
                disabled={refreshingContract || !submissionDetail?.base_project_id}
              >
                {refreshingContract ? <LoadingSpinner size="sm" /> : 'Re-match Recipes'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleRecreateForks}
                disabled={recreatingForks || !submissionDetail?.base_project_id || recreatableCandidates.length === 0}
                title={recreatableCandidates.length === 0 ? 'All matched forks already have custom code' : `Will generate code for ${recreatableCandidates.length} fork(s) without custom code`}
              >
                {recreatingForks ? <LoadingSpinner size="sm" /> : `Generate Fork Code (${recreatableCandidates.length})`}
              </button>
            </div>
          </form>

          <div className="card p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-theme-text">Saved Submissions</h2>
              <button onClick={loadSubmissions} className="btn btn-secondary" disabled={loadingSubmissions}>
                {loadingSubmissions ? <LoadingSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
                Refresh
              </button>
            </div>

            <div className="space-y-2 max-h-[420px] overflow-auto">
              {submissions.map((submission) => (
                <button
                  key={submission.id}
                  onClick={() => setSelectedSubmissionId(submission.id)}
                  className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${
                    selectedSubmissionId === submission.id
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-theme-border-muted hover:bg-theme-surface/40'
                  }`}
                >
                  <div className="text-sm font-semibold text-theme-text">{submission.base_tool_name}</div>
                  <div className="text-xs text-theme-text-muted mt-1">Updated {formatDateTime(submission.updated_at)}</div>
                </button>
              ))}
              {!loadingSubmissions && submissions.length === 0 && (
                <p className="text-sm text-theme-text-secondary">No saved submissions yet.</p>
              )}
            </div>
          </div>
        </div>

        {submissionDetail && (
          <div className="card p-4 sm:p-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-theme-text">Hidden Base Mini-App</h2>
                <p className="text-sm text-theme-text-secondary">
                  Base project: {submissionDetail.base_project_id || 'not created yet'}
                </p>
              </div>
              <div className="flex gap-2">
                {submissionDetail.base_project_id && (
                  <Link to={`/project/${submissionDetail.base_project_id}`} className="btn btn-secondary" target="_blank">
                    Open base project <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-sm">
              <div className="rounded-lg border border-theme-border-muted bg-theme-surface/40 px-4 py-3">
                <div className="text-theme-text-muted">Candidates</div>
                <div className="text-2xl font-semibold text-theme-text">{candidates.length}</div>
              </div>
              <div className="rounded-lg border border-theme-border-muted bg-theme-surface/40 px-4 py-3">
                <div className="text-theme-text-muted">Matched</div>
                <div className="text-2xl font-semibold text-emerald-400">{matchedCandidates.length}</div>
              </div>
              <div className="rounded-lg border border-theme-border-muted bg-theme-surface/40 px-4 py-3">
                <div className="text-theme-text-muted">Unmatched</div>
                <div className="text-2xl font-semibold text-amber-300">{unmatchedCandidates.length}</div>
              </div>
              <div className="rounded-lg border border-theme-border-muted bg-theme-surface/40 px-4 py-3">
                <div className="text-theme-text-muted">Custom code</div>
                <div className="text-2xl font-semibold text-blue-400">
                  {candidates.filter((c) => c.has_custom_code).length}
                </div>
              </div>
              <div className="rounded-lg border border-theme-border-muted bg-theme-surface/40 px-4 py-3">
                <div className="text-theme-text-muted">Recipes refreshed</div>
                <div className="text-sm font-semibold text-theme-text">{formatDateTime(submissionDetail.last_contract_refresh_at)}</div>
              </div>
            </div>

            {baseStatus.length > 0 && (
              <div className="rounded-lg border border-theme-border-muted bg-theme-surface/40 px-3 py-2 text-xs text-theme-text-secondary max-h-56 overflow-auto space-y-1">
                {baseStatus.map((line, index) => <div key={`${line}-${index}`}>{line}</div>)}
              </div>
            )}

            {(forkStatus.length > 0 || forkProgress || forkCoderLines.length > 0) && (
              <div className="space-y-3">
                {forkProgress && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-theme-text">
                        Fork {forkProgress.current}/{forkProgress.total}: {forkProgress.title}
                      </span>
                      <span className="text-theme-text-muted">
                        {Math.round((forkProgress.current / forkProgress.total) * 100)}%
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-theme-surface overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                        style={{ width: `${(forkProgress.current / forkProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {forkCoderLines.length > 0 && (
                  <div className="rounded-lg border border-theme-border-muted bg-black/80 px-3 py-2 font-mono text-xs text-green-400 max-h-64 overflow-auto">
                    {forkCoderLines.map((line, index) => (
                      <div key={index} className="whitespace-pre-wrap break-all">{line}</div>
                    ))}
                  </div>
                )}

                {forkStatus.length > 0 && (
                  <details open={!forkProgress} className="rounded-lg border border-theme-border-muted bg-theme-surface/40 px-3 py-2">
                    <summary className="cursor-pointer text-xs font-medium text-theme-text-secondary">
                      Completed forks ({forkStatus.length})
                    </summary>
                    <div className="mt-2 text-xs text-theme-text-secondary max-h-40 overflow-auto space-y-1">
                      {forkStatus.map((line, index) => <div key={`${line}-${index}`}>{line}</div>)}
                    </div>
                  </details>
                )}
              </div>
            )}

            {loadingDetail ? (
              <div className="py-8 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-theme-text-muted" /></div>
            ) : (
              <details className="rounded-lg border border-theme-border-muted bg-theme-surface/30 px-4 py-3">
                <summary className="cursor-pointer text-sm font-medium text-theme-text-secondary">
                  View recipes snapshot ({recipeContracts.length} recipes)
                </summary>
                <pre className="mt-3 whitespace-pre-wrap text-xs text-theme-text-secondary overflow-auto">
                  {submissionDetail.backend_contract ? JSON.stringify(submissionDetail.backend_contract, null, 2) : 'No recipes loaded yet.'}
                </pre>
              </details>
            )}
          </div>
        )}

        {submissionDetail && (
          <div className="card p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-theme-text">Fork Candidates</h2>
                <p className="text-sm text-theme-text-secondary">
                  Review title, description, slug, and recipe matching before recreating the fork mini-apps.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {candidates.map((candidate) => (
                <div key={candidate.id} className="rounded-xl border border-theme-border-muted p-4 space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-theme-text-muted">Original idea</div>
                      <div className="font-medium text-theme-text">{candidate.original_idea}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className={`rounded-full px-2.5 py-1 ${
                        candidate.recipe_match_status === 'matched'
                          ? 'bg-emerald-500/15 text-emerald-400'
                          : candidate.recipe_match_status === 'needs_backend_extension'
                            ? 'bg-amber-500/15 text-amber-300'
                            : 'bg-theme-surface text-theme-text-muted'
                      }`}>
                        {candidate.recipe_match_status}
                      </span>
                      {candidate.project_id && (
                        <span className={`rounded-full px-2.5 py-1 ${
                          candidate.has_custom_code
                            ? 'bg-blue-500/15 text-blue-400'
                            : 'bg-theme-surface text-theme-text-muted'
                        }`}>
                          {candidate.has_custom_code ? 'Custom code' : 'Base code'}
                        </span>
                      )}
                      {candidate.recipe_id && (
                        <span className="rounded-full bg-theme-surface px-2.5 py-1 text-theme-text-muted">
                          recipe: {candidate.recipe_id}
                        </span>
                      )}
                      {candidate.project_id && (
                        <Link to={`/project/${candidate.project_id}`} target="_blank" className="btn btn-secondary">
                          Open fork <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs font-medium text-theme-text-secondary">Title</span>
                      <input
                        className="input mt-1"
                        value={candidate.title}
                        onChange={(e) => setCandidates((prev) => prev.map((item) => item.id === candidate.id ? { ...item, title: e.target.value } : item))}
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-theme-text-secondary">Slug</span>
                      <input
                        className="input mt-1"
                        value={candidate.slug}
                        onChange={(e) => setCandidates((prev) => prev.map((item) => item.id === candidate.id ? { ...item, slug: slugify(e.target.value) } : item))}
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="text-xs font-medium text-theme-text-secondary">Description</span>
                    <textarea
                      className="input mt-1 min-h-[96px]"
                      value={candidate.description}
                      onChange={(e) => setCandidates((prev) => prev.map((item) => item.id === candidate.id ? { ...item, description: e.target.value } : item))}
                    />
                  </label>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs font-medium text-theme-text-secondary">Logo emoji</span>
                      <input
                        className="input mt-1"
                        value={candidate.logo_emoji || ''}
                        onChange={(e) => setCandidates((prev) => prev.map((item) => item.id === candidate.id ? { ...item, logo_emoji: e.target.value } : item))}
                        placeholder="Optional"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-theme-text-secondary">Recipe ID (read-only, set by matching)</span>
                      <input
                        className="input mt-1 bg-theme-surface/40"
                        value={candidate.recipe_id || ''}
                        readOnly
                        tabIndex={-1}
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="text-xs font-medium text-theme-text-secondary">Frontend prompt override</span>
                    <textarea
                      className="input mt-1 min-h-[120px]"
                      value={candidate.frontend_prompt_override || ''}
                      onChange={(e) => setCandidates((prev) => prev.map((item) => item.id === candidate.id ? { ...item, frontend_prompt_override: e.target.value } : item))}
                      placeholder="Optional. Leave blank to generate the frontend prompt from title, description, and matched recipe."
                    />
                  </label>

                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={candidate.enabled}
                        onChange={(e) => setCandidates((prev) => prev.map((item) => item.id === candidate.id ? { ...item, enabled: e.target.checked } : item))}
                      />
                      Enabled
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={candidate.publish}
                        onChange={(e) => setCandidates((prev) => prev.map((item) => item.id === candidate.id ? { ...item, publish: e.target.checked } : item))}
                      />
                      Publish after recreation
                    </label>
                  </div>

                  <div className="rounded-lg border border-theme-border-muted bg-theme-surface/40 px-3 py-2 text-xs text-theme-text-secondary">
                    <div>{candidate.recipe_match_notes || 'No recipe mapping note yet.'}</div>
                    {candidate.backend_api_description && (
                      <details className="mt-2">
                        <summary className="cursor-pointer">Show matched recipe details</summary>
                        <pre className="mt-2 whitespace-pre-wrap overflow-auto">{candidate.backend_api_description}</pre>
                      </details>
                    )}
                  </div>

                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleSaveCandidate(candidate)}
                    disabled={savingCandidateId === candidate.id}
                  >
                    {savingCandidateId === candidate.id ? <LoadingSpinner size="sm" /> : 'Save Candidate'}
                  </button>
                </div>
              ))}
              {candidates.length === 0 && (
                <p className="text-sm text-theme-text-secondary">Save ideas to populate fork candidates.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
